import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2 } from "lucide-react";

export default function OrgPasswordReset() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { orgs } = useApp();
    const token = params.get("token") || "";

    const [slug, setSlug] = useState(params.get("slug") || "");
    const [requestBusy, setRequestBusy] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [emailHint, setEmailHint] = useState("");

    const [tokenState, setTokenState] = useState(token ? "checking" : "none"); // checking | valid | invalid | none
    const [orgName, setOrgName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [done, setDone] = useState(false);

    const selectableOrgs = useMemo(
        () => [...orgs].filter((o) => o.status !== "pending").sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [orgs],
    );

    useEffect(() => {
        if (!token) return;
        api.validateOrgPasswordReset(token)
            .then((r) => {
                if (r?.ok) {
                    setTokenState("valid");
                    setOrgName(r.org_name || r.slug || "");
                } else {
                    setTokenState("invalid");
                }
            })
            .catch(() => setTokenState("invalid"));
    }, [token]);

    const submitRequest = async (e) => {
        e.preventDefault();
        if (!slug) {
            toast.error("Choose your organisation first");
            return;
        }
        setRequestBusy(true);
        try {
            const r = await api.requestOrgPasswordReset(slug);
            setRequestSent(true);
            setEmailHint(r?.email_hint || "");
        } catch {
            setRequestSent(true);
        } finally {
            setRequestBusy(false);
        }
    };

    const submitConfirm = async (e) => {
        e.preventDefault();
        if (password.trim().length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setConfirmBusy(true);
        try {
            await api.confirmOrgPasswordReset({ token, new_password: password.trim() });
            setDone(true);
            toast.success("Password updated — you can now sign in");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reset the password");
        } finally {
            setConfirmBusy(false);
        }
    };

    return (
        <div data-testid="org-password-reset-page" className="max-w-xl mx-auto px-4 sm:px-6 py-16">
            <div className="mb-8 text-center">
                <div className="h-14 w-14 mx-auto rounded-full bg-primary/10 text-primary grid place-items-center">
                    <KeyRound className="h-7 w-7" />
                </div>
                <h1 className="font-display font-black text-3xl sm:text-4xl mt-4">Reset organisation password</h1>
            </div>

            {done ? (
                <div className="rounded-3xl border border-border bg-surface p-8 text-center">
                    <CheckCircle2 className="h-10 w-10 mx-auto text-secondary" />
                    <p className="mt-4 text-sm text-muted-foreground">
                        Your password has been updated.
                    </p>
                    <button
                        onClick={() => navigate("/organisation-dashboard")}
                        className="mt-6 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm"
                    >
                        Go to dashboard sign-in
                    </button>
                </div>
            ) : token ? (
                <div className="rounded-3xl border border-border bg-surface p-8">
                    {tokenState === "checking" && (
                        <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking your reset link…
                        </p>
                    )}
                    {tokenState === "invalid" && (
                        <div className="text-sm">
                            <p className="font-semibold">This reset link is invalid or has expired.</p>
                            <p className="text-muted-foreground mt-2">
                                Links can only be used once and expire after 60 minutes.{" "}
                                <Link to="/organisation/reset-password" className="text-primary font-semibold hover:underline">
                                    Request a new one
                                </Link>
                                .
                            </p>
                        </div>
                    )}
                    {tokenState === "valid" && (
                        <form onSubmit={submitConfirm} className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Set a new password for <strong className="text-foreground">{orgName}</strong>.
                            </p>
                            <label className="text-sm block">
                                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New password</span>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    data-testid="reset-new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-1 w-full px-3 py-2.5 rounded-2xl border border-border bg-background"
                                />
                            </label>
                            <label className="text-sm block">
                                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm password</span>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    data-testid="reset-confirm-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="mt-1 w-full px-3 py-2.5 rounded-2xl border border-border bg-background"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={confirmBusy}
                                data-testid="reset-submit"
                                className="w-full px-5 py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60"
                            >
                                {confirmBusy ? "Saving…" : "Set new password"}
                            </button>
                        </form>
                    )}
                </div>
            ) : requestSent ? (
                <div className="rounded-3xl border border-border bg-surface p-8 text-center">
                    <CheckCircle2 className="h-10 w-10 mx-auto text-secondary" />
                    <p className="mt-4 text-sm text-muted-foreground">
                        If this organisation has a contact email on file, a reset link is on its way
                        {emailHint ? <> to <strong className="text-foreground">{emailHint}</strong></> : null}.
                        The link expires in 60 minutes.
                    </p>
                </div>
            ) : (
                <form onSubmit={submitRequest} className="rounded-3xl border border-border bg-surface p-8 space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Choose your organisation and we'll email a reset link to its registered contact address.
                    </p>
                    <label className="text-sm block">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organisation</span>
                        <select
                            data-testid="reset-org-select"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            className="mt-1 w-full px-3 py-2.5 rounded-2xl border border-border bg-background"
                        >
                            <option value="">Choose…</option>
                            {selectableOrgs.map((o) => (
                                <option key={o.slug} value={o.slug}>{o.name}</option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="submit"
                        disabled={requestBusy}
                        data-testid="reset-request-submit"
                        className="w-full px-5 py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60"
                    >
                        {requestBusy ? "Sending…" : "Email me a reset link"}
                    </button>
                </form>
            )}
        </div>
    );
}
